import { useState } from 'react'
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Badge,
  Input,
  Grid,
  GridItem,
  Card,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { Clock, User, Database, Activity } from 'lucide-react'
import { useAuditLogs } from '@/shared/hooks/useAuditLog'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'

const OPERATION_COLORS = {
  INSERT: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
} as const

const OPERATION_LABELS = {
  INSERT: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
} as const

const TABLE_LABELS: Record<string, string> = {
  agencies: 'Agences',
  residences: 'Résidences',
  units: 'Lots',
  owners: 'Copropriétaires',
  ownerships: 'Propriétés',
  documents: 'Documents',
  users: 'Utilisateurs',
}

export function AuditLogsPage() {
  const [tableName, setTableName] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: logs, isLoading, error, refetch } = useAuditLogs({
    tableName: tableName || undefined,
    limit: 200,
  })

  const filteredLogs = logs?.filter((log) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      log.user_email?.toLowerCase().includes(searchLower) ||
      log.record_id.toLowerCase().includes(searchLower) ||
      log.table_name.toLowerCase().includes(searchLower)
    )
  })

  if (error) {
    return <ErrorState message="Erreur lors du chargement des logs" onRetry={refetch} />
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg">Journal d'audit</Heading>
          <Text fontSize="sm" color="gray.600" mt={1}>
            Historique complet des modifications de données
          </Text>
        </Box>
      </HStack>

      {/* Statistiques rapides */}
      <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
        <GridItem>
          <Card.Root p={4}>
            <HStack>
              <Box p={2} bg="blue.100" borderRadius="md" color="blue.600">
                <Activity size={20} />
              </Box>
              <Box>
                <Text fontSize="2xl" fontWeight="bold">
                  {logs?.length || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Total d'événements
                </Text>
              </Box>
            </HStack>
          </Card.Root>
        </GridItem>

        <GridItem>
          <Card.Root p={4}>
            <HStack>
              <Box p={2} bg="green.100" borderRadius="md" color="green.600">
                <Database size={20} />
              </Box>
              <Box>
                <Text fontSize="2xl" fontWeight="bold">
                  {logs?.filter((l) => l.operation === 'INSERT').length || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Créations
                </Text>
              </Box>
            </HStack>
          </Card.Root>
        </GridItem>

        <GridItem>
          <Card.Root p={4}>
            <HStack>
              <Box p={2} bg="yellow.100" borderRadius="md" color="yellow.600">
                <Database size={20} />
              </Box>
              <Box>
                <Text fontSize="2xl" fontWeight="bold">
                  {logs?.filter((l) => l.operation === 'UPDATE').length || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Modifications
                </Text>
              </Box>
            </HStack>
          </Card.Root>
        </GridItem>

        <GridItem>
          <Card.Root p={4}>
            <HStack>
              <Box p={2} bg="red.100" borderRadius="md" color="red.600">
                <Database size={20} />
              </Box>
              <Box>
                <Text fontSize="2xl" fontWeight="bold">
                  {logs?.filter((l) => l.operation === 'DELETE').length || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Suppressions
                </Text>
              </Box>
            </HStack>
          </Card.Root>
        </GridItem>
      </Grid>

      {/* Filtres */}
      <Card.Root p={4} mb={6}>
        <HStack gap={4}>
          <Field.Root flex={1}>
            <Field.Label>Rechercher</Field.Label>
            <Input
              placeholder="Email, ID, table..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Field.Root>

          <Field.Root flex={1}>
            <Field.Label>Table</Field.Label>
            <NativeSelectRoot>
              <NativeSelectField
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
              >
                <option value="">Toutes les tables</option>
                {Object.entries(TABLE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
          </Field.Root>
        </HStack>
      </Card.Root>

      {/* Liste des logs */}
      <Card.Root>
        {isLoading ? (
          <Box p={8}>
            <LoadingState message="Chargement des logs..." />
          </Box>
        ) : filteredLogs && filteredLogs.length > 0 ? (
          <VStack align="stretch" gap={0} divideY="1px">
            {filteredLogs.map((log) => (
              <Box key={log.id} p={4} _hover={{ bg: 'gray.50' }}>
                <HStack justify="space-between" mb={2}>
                  <HStack gap={3}>
                    <Badge
                      colorPalette={OPERATION_COLORS[log.operation]}
                      size="sm"
                    >
                      {OPERATION_LABELS[log.operation]}
                    </Badge>
                    <HStack gap={2}>
                      <Database size={14} />
                      <Text fontSize="sm" fontWeight="medium">
                        {TABLE_LABELS[log.table_name] || log.table_name}
                      </Text>
                    </HStack>
                  </HStack>

                  <HStack gap={4} fontSize="xs" color="gray.600">
                    <HStack gap={1}>
                      <User size={12} />
                      <Text>{log.user_email || 'Système'}</Text>
                    </HStack>
                    <HStack gap={1}>
                      <Clock size={12} />
                      <Text>
                        {new Date(log.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </Text>
                    </HStack>
                  </HStack>
                </HStack>

                {log.changed_fields && log.changed_fields.length > 0 && (
                  <Box mt={2}>
                    <Text fontSize="xs" color="gray.600">
                      Champs modifiés:{' '}
                      <Text as="span" fontWeight="medium" color="gray.900">
                        {log.changed_fields
                          .filter(
                            (field) =>
                              !['updated_at', 'updated_by'].includes(field)
                          )
                          .join(', ')}
                      </Text>
                    </Text>
                  </Box>
                )}

                <Text fontSize="xs" color="gray.500" mt={1} fontFamily="mono">
                  ID: {log.record_id}
                </Text>
              </Box>
            ))}
          </VStack>
        ) : (
          <Box p={8} textAlign="center">
            <Text color="gray.600">Aucun log trouvé</Text>
          </Box>
        )}
      </Card.Root>
    </Box>
  )
}
